import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { isNetworkError } from "@src/appV2/Errors/errorUtils";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { getShiftPath, type GetShiftResponse } from "@src/appV2/Shifts/Shift/api/useGetShift";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import {
  TIMEKEEPING_NETWORK_RETRY_BASE_DELAY_MS,
  TIMEKEEPING_NETWORK_RETRY_MAX_ATTEMPTS,
  TIMEKEEPING_NETWORK_RETRY_MAX_DELAY_MS,
} from "../constants";
import { logTimekeepingNetworkRetryAttempt } from "./logTimekeepingNetworkRetryAttempt";

interface UseTimekeepingNetworkRetryOptionsParams<TVariables> {
  source: string;
  getShiftId: (variables: TVariables) => string | undefined;
  getAction: (variables: TVariables) => string | undefined;
  /**
   * Determines whether a given error should trigger a retry.
   * Defaults to `isNetworkError`, which retries only when the request
   * never reached the server (no response object on the Axios error).
   */
  shouldRetry?: (error: unknown) => boolean;
}

interface TimekeepingNetworkRetryMutationOptions<TVariables> {
  onMutate: (variables: TVariables) => void;
  retry: (failureCount: number, error: unknown) => boolean;
  retryDelay: (failureCount: number) => number;
}

/**
 * Returns React Query `useMutation` options that add automatic retry behavior
 * for transient errors (by default, network errors only).
 *
 * **How it works:**
 * Delegates entirely to React Query's built-in `retry` and `retryDelay` callbacks.
 * The retry model is **count-based**: it retries up to `TIMEKEEPING_NETWORK_RETRY_MAX_ATTEMPTS`
 * times using exponential backoff capped at `TIMEKEEPING_NETWORK_RETRY_MAX_DELAY_MS`.
 * Each retry attempt is logged via `logTimekeepingNetworkRetryAttempt`, which reads
 * `workerId` and `workplaceId` from the React Query cache to enrich the event.
 *
 * **What it does not support:**
 * - Time-based retry budgets or multi-phase retry intervals
 * - UI state to reflect the current retry phase
 * - Cancellation of an in-progress retry loop
 * - In-flight request deduplication across multiple `mutate` calls
 *
 * For those requirements, see `useExtendedTimekeepingRetry`.
 *
 * **Usage:**
 * ```ts
 * const networkRetryOptions = useTimekeepingNetworkRetryOptions<MyVariables>({
 *   source: "my-source",
 *   getShiftId: (v) => v.shiftId,
 *   getAction: (v) => v.action,
 * });
 *
 * return useMutation({ ...networkRetryOptions, mutationFn: ... });
 * ```
 */
export function useTimekeepingNetworkRetryOptions<TVariables>(
  params: UseTimekeepingNetworkRetryOptionsParams<TVariables>
): TimekeepingNetworkRetryMutationOptions<TVariables> {
  const { source, getShiftId, getAction, shouldRetry = isNetworkError } = params;
  const queryClient = useQueryClient();
  const variablesRef = useRef<TVariables | undefined>(undefined);

  return {
    onMutate: (variables: TVariables) => {
      variablesRef.current = variables;
    },
    retry: (failureCount: number, error: unknown) => {
      if (!shouldRetry(error)) {
        return false;
      }

      if (failureCount >= TIMEKEEPING_NETWORK_RETRY_MAX_ATTEMPTS) {
        const shiftId = isDefined(variablesRef.current)
          ? getShiftId(variablesRef.current)
          : undefined;
        const cachedShift = shiftId
          ? queryClient.getQueryData<GetShiftResponse>([
              `${environmentConfig.REACT_APP_BASE_API_URL}${getShiftPath(shiftId)}`,
              undefined,
            ])
          : undefined;
        logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_NETWORK_RETRY_EXHAUSTED, {
          source,
          failureCount,
          shiftId,
          action: isDefined(variablesRef.current) ? getAction(variablesRef.current) : undefined,
          workplaceId: cachedShift?.response.facilityId,
          workerId: cachedShift?.response.agentId,
        });
        return false;
      }

      logTimekeepingNetworkRetryAttempt({
        queryClient,
        shiftId: isDefined(variablesRef.current) ? getShiftId(variablesRef.current) : undefined,
        source,
        attempt: failureCount + 1,
        action: isDefined(variablesRef.current) ? getAction(variablesRef.current) : undefined,
      });

      return true;
    },
    retryDelay: (failureCount: number) =>
      Math.min(
        TIMEKEEPING_NETWORK_RETRY_BASE_DELAY_MS * 2 ** failureCount,
        TIMEKEEPING_NETWORK_RETRY_MAX_DELAY_MS
      ),
  };
}
